import { API_BASE_URL, API_ENDPOINTS } from './config';
import type { ApiResponse, Product, ProductSpecifications, Category, Pagination, FavoriteItem, CartItem, CartItemDetail, User, CreateUserData, UpdateUserData, UserOrder, SellerProfile } from './types';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
        }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      
      // Сервер возвращает структуру: { success: true, data: [...], pagination: {...} }
      // Мы распаковываем её, чтобы вернуть: { success: true, data: [...], pagination: {...} }
      return {
        success: true,
        ...data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Health check
  async checkHealth(): Promise<ApiResponse<{ status: string; timestamp: string; database: any }>> {
    return this.request(API_ENDPOINTS.health);
  }

  // Categories
  async getCategories(params?: { level?: number; parent_id?: string; active_only?: boolean }): Promise<ApiResponse<Category[]>> {
    const queryParams = new URLSearchParams();
    if (params?.level !== undefined) queryParams.append('level', params.level.toString());
    if (params?.parent_id) queryParams.append('parent_id', params.parent_id);
    if (params?.active_only !== undefined) queryParams.append('active_only', params.active_only.toString());
    
    const queryString = queryParams.toString();
    return this.request(`${API_ENDPOINTS.categories}${queryString ? `?${queryString}` : ''}`);
  }

  async getCategory(id: string): Promise<ApiResponse<Category & { path: Category[] }>> {
    return this.request(API_ENDPOINTS.categories + `/${id}`);
  }

  async getCategoryProducts(
    categoryId: string,
    params?: { page?: number; limit?: number; status?: string }
  ): Promise<ApiResponse<string[]>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    return this.request(`${API_ENDPOINTS.categories}/${categoryId}/products${queryString ? `?${queryString}` : ''}`);
  }

  // Products
  async getProducts(params?: {
    page?: number;
    limit?: number;
    category_id?: string;
    status?: string;
    active_only?: boolean;
    q?: string;
  }): Promise<ApiResponse<Product[]>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category_id) queryParams.append('category_id', params.category_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.active_only !== undefined) queryParams.append('active_only', params.active_only.toString());
    if (params?.q) queryParams.append('q', params.q);
    
    const queryString = queryParams.toString();
    return this.request(`${API_ENDPOINTS.products}${queryString ? `?${queryString}` : ''}`);
  }

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    return this.request(API_ENDPOINTS.product(id));
  }

  async getSeller(id: string): Promise<ApiResponse<SellerProfile>> {
    return this.request(API_ENDPOINTS.seller(id));
  }

  async getProductSpecifications(id: string): Promise<ApiResponse<ProductSpecifications>> {
    return this.request(API_ENDPOINTS.productSpecifications(id));
  }

  async getProductsBatch(ids: string[]): Promise<ApiResponse<Product[]>> {
    if (ids.length === 0) {
      return {
        success: false,
        error: 'IDs array cannot be empty',
      };
    }
    if (ids.length > 100) {
      return {
        success: false,
        error: 'Maximum 100 products per batch request',
      };
    }
    return this.request(API_ENDPOINTS.productBatch, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Users
  async getUser(id: string): Promise<ApiResponse<User>> {
    return this.request(API_ENDPOINTS.users(id));
  }

  async getUserByPhone(phone: string): Promise<ApiResponse<User>> {
    return this.request(API_ENDPOINTS.userByPhone(phone));
  }

  async createUser(userData: CreateUserData): Promise<ApiResponse<User>> {
    return this.request(API_ENDPOINTS.userCreate, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<ApiResponse<User>> {
    return this.request(API_ENDPOINTS.userUpdate(userId), {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async getUserOrders(userId: string): Promise<ApiResponse<UserOrder[]>> {
    return this.request(API_ENDPOINTS.userOrders(userId));
  }

  // Favorites
  async getUserFavorites(id: string): Promise<ApiResponse<string[]>> {
    return this.request(API_ENDPOINTS.userFavorites(id));
  }

  async addToFavorites(userId: string, productId: string): Promise<ApiResponse<FavoriteItem>> {
    return this.request(API_ENDPOINTS.userFavoritesAdd(userId), {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  async removeFromFavorites(userId: string, productId: string): Promise<ApiResponse<{ user_id: string; product_id: string }>> {
    return this.request(API_ENDPOINTS.userFavoritesRemove(userId, productId), {
      method: 'DELETE',
    });
  }

  // Cart
  async getUserCart(id: string): Promise<ApiResponse<{ data: string[]; items: CartItem[]; count: number; user_id: string }>> {
    const response = await this.request<any>(API_ENDPOINTS.userCart(id));
    
    // Сервер возвращает плоскую структуру: { success: true, user_id, count, data, items }
    // Нужно обернуть её в data для соответствия типу ApiResponse
    if (response.success && response.user_id !== undefined) {
      return {
        success: true,
        data: {
          data: response.data || [],
          items: response.items || [],
          count: response.count || 0,
          user_id: response.user_id,
        },
      };
    }
    
    return response as ApiResponse<{ data: string[]; items: CartItem[]; count: number; user_id: string }>;
  }

  async addToCart(userId: string, productId: string, quantity: number = 1): Promise<ApiResponse<CartItemDetail>> {
    return this.request(API_ENDPOINTS.userCartAdd(userId), {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity }),
    });
  }

  async updateCartItem(userId: string, productId: string, quantity: number): Promise<ApiResponse<CartItemDetail>> {
    return this.request(API_ENDPOINTS.userCartUpdate(userId, productId), {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async removeFromCart(userId: string, productId: string): Promise<ApiResponse<{ user_id: string; product_id: string }>> {
    return this.request(API_ENDPOINTS.userCartRemove(userId, productId), {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
